import { supabase } from '../lib/supabase';
import { isValidUUID, ensureUUID } from '../utils/uuidGenerator';

export const salaryInsightsService = {
  // Fetch salary data for an occupation and ZIP code
  async getSalaryInsights(occupationId, zipCode, userRole = 'free') {
    try {
      const response = await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/salary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          occupationId,
          zipCode,
          userRole
        })
      });

      if (!response?.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response?.json();
      return result;
    } catch (error) {
      console.error('Salary insights fetch error:', error);
      throw error;
    }
  },

  // Update user's current ZIP code
  async updateCurrentZip(userId, zipCode) {
    try {
      // Validate and ensure proper UUID format
      if (!userId) {
        throw new Error('User ID is required');
      }

      const validUserId = ensureUUID(userId);
      
      // Skip database operation if UUID is invalid (mock user)
      if (!isValidUUID(validUserId)) {
        console.warn('Invalid UUID format, skipping database update:', validUserId);
        return { current_zip: zipCode };
      }

      const { data, error } = await supabase?.from('user_profiles')?.update({ current_zip: zipCode })?.eq('id', validUserId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update current ZIP error:', error);
      throw error;
    }
  },

  // Update user's target ZIP code
  async updateTargetZip(userId, zipCode) {
    try {
      // Validate and ensure proper UUID format
      if (!userId) {
        throw new Error('User ID is required');
      }

      const validUserId = ensureUUID(userId);
      
      // Skip database operation if UUID is invalid (mock user)
      if (!isValidUUID(validUserId)) {
        console.warn('Invalid UUID format, skipping database update:', validUserId);
        return { target_zip: zipCode };
      }

      const { data, error } = await supabase?.from('user_profiles')?.update({ target_zip: zipCode })?.eq('id', validUserId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update target ZIP error:', error);
      throw error;
    }
  },

  // Get user's saved ZIP codes - Fixed to handle 0 rows gracefully
  async getUserZipCodes(userId) {
    try {
      // Validate and ensure proper UUID format
      if (!userId) {
        console.warn('getUserZipCodes called without userId');
        return {
          currentZip: '',
          targetZip: ''
        };
      }

      const validUserId = ensureUUID(userId);
      
      // Skip database operation if UUID is invalid (mock user)
      if (!isValidUUID(validUserId)) {
        console.warn('Invalid UUID format, returning empty ZIP codes:', validUserId);
        return {
          currentZip: '',
          targetZip: ''
        };
      }

      // Use .maybeSingle() instead of .single() to handle 0 rows gracefully
      const { data, error } = await supabase?.from('user_profiles')?.select('current_zip, target_zip')?.eq('id', validUserId)?.maybeSingle();

      if (error) {
        console.error('Database error in getUserZipCodes:', error);
        // Don't throw for database errors, return empty values
        return {
          currentZip: '',
          targetZip: ''
        };
      }

      // Handle case where user profile doesn't exist (data is null)
      if (!data) {
        console.info('User profile not found, returning empty ZIP codes');
        return {
          currentZip: '',
          targetZip: ''
        };
      }

      return {
        currentZip: data?.current_zip || '',
        targetZip: data?.target_zip || ''
      };
    } catch (error) {
      console.error('Get user ZIP codes error:', error);
      // Return empty values instead of throwing to prevent UI crashes
      return {
        currentZip: '',
        targetZip: ''
      };
    }
  },

  // Calculate salary comparison percentages
  calculateComparison(localSalary, nationalSalary) {
    if (!localSalary || !nationalSalary) return null;
    
    const percentage = ((localSalary - nationalSalary) / nationalSalary) * 100;
    return {
      percentage: Math.round(percentage),
      isHigher: percentage > 0,
      isLower: percentage < 0
    };
  },

  // Format salary for display
  formatSalary(amount, currency = 'USD') {
    if (!amount) return 'N/A';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  },

  // Validate ZIP code format
  validateZipCode(zipCode) {
    const zipRegex = /^\d{5}(-\d{4})?$/;
    return zipRegex?.test(zipCode);
  }
};